import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/client';
import { RouteEntity } from '../types';
import { customerForStop, trackingCodeFor } from '../utils/mockState';
import { useToast } from '../components/Toast';
import './WhatsAppTracking.css';

export interface WhatsAppNotification {
  id: string;
  timestamp: number;
  routeId: string;
  stopIdx: number;
  event: 'route_started' | 'next_stop' | 'arrived' | 'delivered' | 'delayed';
  phone: string;
  customerName: string;
  orderRef: string;
  trackingCode: string;
  trackingUrl: string;
  message: string;
  status: 'sent' | 'delivered' | 'read';
  driverName: string;
  vehiclePlate: string;
}

const STORAGE_KEY = 'otimizarota_whatsapp_notifications';

const EVENT_CONFIG = {
  route_started: {
    label: 'Rota iniciada',
    cls: 'event-start',
    color: '#146276',
    bg: 'rgba(20, 98, 118, 0.1)',
  },
  next_stop: {
    label: 'Próxima parada',
    cls: 'event-next',
    color: '#a97f22',
    bg: 'rgba(233, 196, 106, 0.18)',
  },
  arrived: {
    label: 'Chegou',
    cls: 'event-arrived',
    color: '#0d7a6c',
    bg: 'rgba(42, 157, 143, 0.14)',
  },
  delivered: {
    label: 'Entregue',
    cls: 'event-delivered',
    color: '#128c7e',
    bg: 'rgba(37, 211, 102, 0.14)',
  },
  delayed: {
    label: 'Atrasado',
    cls: 'event-delayed',
    color: '#c1573b',
    bg: 'rgba(231, 111, 81, 0.14)',
  },
};

function formatTimeAgo(ts: number): string {
  const diffSec = Math.round((Date.now() - ts) / 1000);
  if (diffSec < 60) return 'agora';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `há ${diffMin}min`;
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) return `há ${diffHours}h`;
  return `há ${Math.round(diffHours / 24)}d`;
}

function getEventIcon(event: WhatsAppNotification['event']) {
  switch (event) {
    case 'route_started':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="5,3 19,12 5,21 5,3"></polygon>
        </svg>
      );
    case 'next_stop':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="10" r="3"></circle>
          <path d="M12 2a8 8 0 0 0-8 8c0 5.4 8 12 8 12s8-6.6 8-12a8 8 0 0 0-8-8z"></path>
        </svg>
      );
    case 'arrived':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 20 10.2C20 17.5 12 22 12 22z"></path>
          <polyline points="9,10 11,12 15,8"></polyline>
        </svg>
      );
    case 'delivered':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5"></path>
        </svg>
      );
    case 'delayed':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12,6 12,12 16,14"></polyline>
        </svg>
      );
    default:
      return null;
  }
}

export default function WhatsAppTracking() {
  const { showToast } = useToast();
  const [routes, setRoutes] = useState<RouteEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<WhatsAppNotification[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Load notifications from localStorage
  const loadSavedNotifications = useCallback((): WhatsAppNotification[] => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch {
      // ignore
    }
    return [];
  }, []);

  const saveNotifications = useCallback((list: WhatsAppNotification[]) => {
    setNotifications(list);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      window.dispatchEvent(new Event('otimizarota-notifications-changed'));
    } catch {
      // ignore
    }
  }, []);

  // Fetch routes from API
  useEffect(() => {
    api.get<RouteEntity[]>('/routes')
      .then(({ data }) => setRoutes(data))
      .catch(() => setRoutes([]))
      .finally(() => setLoading(false));
  }, []);

  // Initialize notifications from storage or derive initial ones
  useEffect(() => {
    const saved = loadSavedNotifications();
    if (saved && saved.length > 0) {
      setNotifications(saved);
      if (!selectedId) setSelectedId(saved[0].id);
    }
  }, [loadSavedNotifications, selectedId]);

  // Generate demo notification data
  const handleSeedDemo = useCallback(() => {
    const existing = loadSavedNotifications();
    const demoRoutes = routes.length > 0 ? routes.slice(0, 4) : [
      {
        id: 'r_demo_1',
        name: 'Rota Alphaville — Coleta & Entregas',
        status: 'em_andamento' as const,
        vehicle: { id: 'v1', name: 'Van Delivery 01', plate: 'ABC-1234', driverName: 'Marcos R.' },
        stops: [
          { id: 's1', deliveryPoint: { title: 'CD Matriz São Paulo', address: 'Av. Paulista, 1578', city: 'São Paulo' }, status: 'concluida' as const },
          { id: 's2', deliveryPoint: { title: 'Hospital Nove de Julho', address: 'Rua Peixoto Gomide, 625', city: 'São Paulo' }, status: 'concluida' as const },
          { id: 's3', deliveryPoint: { title: 'Restaurante Jardins', address: 'Rua Oscar Freire, 725', city: 'São Paulo' }, status: 'concluida' as const },
          { id: 's4', deliveryPoint: { title: 'Clínica Paulista', address: 'Av. Brigadeiro Luís Antônio, 2013', city: 'São Paulo' }, status: 'pendente' as const },
        ]
      },
      {
        id: 'r_demo_2',
        name: 'Zona Sul — 15/08 Manhã',
        status: 'em_andamento' as const,
        vehicle: { id: 'v2', name: 'Furgão Urbano 02', plate: 'XYZ-9876', driverName: 'Ana Paula' },
        stops: [
          { id: 's5', deliveryPoint: { title: 'CD Distribuição Sul', address: 'Av. Ibirapuera, 3103', city: 'São Paulo' }, status: 'concluida' as const },
          { id: 's6', deliveryPoint: { title: 'Supermercado Central', address: 'Av. Indianópolis, 1200', city: 'São Paulo' }, status: 'concluida' as const },
          { id: 's7', deliveryPoint: { title: 'Farmácia Vida', address: 'Rua Domingos de Morais, 2564', city: 'São Paulo' }, status: 'pendente' as const },
        ]
      }
    ] as unknown as RouteEntity[];

    const events: Array<'route_started' | 'next_stop' | 'arrived' | 'delivered'> = ['route_started', 'next_stop', 'arrived', 'delivered'];
    let baseTime = Date.now() - 45 * 60 * 1000;
    const generated: WhatsAppNotification[] = [];

    demoRoutes.forEach((r, ri) => {
      const driverName = r.vehicle?.driverName || (ri % 2 === 0 ? 'Marcos R.' : 'Ana Paula');
      const vehiclePlate = r.vehicle?.plate || (ri % 2 === 0 ? 'ABC-1234' : 'XYZ-9876');
      const stops = r.stops || [];
      const count = Math.max(2, Math.min(stops.length, 4));

      for (let i = 1; i <= count; i++) {
        const stop = stops[i] || stops[0] || {
          deliveryPoint: { title: `Destino ${i}`, address: `Rua Exemplo ${i * 100}`, city: 'São Paulo' }
        };
        const cust = customerForStop({ address: stop.deliveryPoint.address });
        const trackCode = trackingCodeFor(r.id, i);
        const trackingUrl = `/track/${trackCode}`;
        const ev = events[Math.min(i - 1, events.length - 1)];
        const eta = 8 + Math.floor(Math.random() * 22);

        const templates = {
          route_started: `Olá ${cust.name.split(' ')[0]}! 👋\n\nSeu pedido ${cust.orderRef} saiu para entrega. Seu motorista é *${driverName}* e chegará em aproximadamente *${eta}min*.\n\nAcompanhe em tempo real: ${trackingUrl}`,
          next_stop: `${cust.name.split(' ')[0]}, seu pedido ${cust.orderRef} é a *próxima parada* do motorista! 📍\n\nETA: ${eta}min.\nAcompanhe: ${trackingUrl}`,
          arrived: `Seu motorista *chegou* no endereço, ${cust.name.split(' ')[0]}! 🚚\n\n${driverName} está aguardando com seu pedido ${cust.orderRef}.`,
          delivered: `Entrega concluída, ${cust.name.split(' ')[0]}! ✅\n\nSeu pedido ${cust.orderRef} foi entregue com sucesso. Obrigado por escolher a Express ABC.\n\nComo foi a experiência? https://express.link/nps/${cust.orderRef}`,
          delayed: `Aviso importante: seu pedido ${cust.orderRef} está com *15min de atraso* devido ao trânsito.\n\nNovo ETA: ${eta}min. Acompanhe: ${trackingUrl}`,
        };

        const notif: WhatsAppNotification = {
          id: 'n' + (baseTime + i * 1000) + '_' + Math.random().toString(36).slice(2, 7),
          timestamp: baseTime,
          routeId: r.id,
          stopIdx: i,
          event: ev,
          phone: cust.phone,
          customerName: cust.name,
          orderRef: cust.orderRef,
          trackingCode: trackCode,
          trackingUrl,
          message: templates[ev],
          status: i === 1 ? 'read' : i === 2 ? 'delivered' : 'sent',
          driverName,
          vehiclePlate,
        };

        generated.unshift(notif);
        baseTime += (3 + Math.random() * 7) * 60 * 1000;
      }
    });

    const combined = [...generated, ...existing];
    saveNotifications(combined);
    if (combined.length > 0) setSelectedId(combined[0].id);
    showToast(`${generated.length} notificações de demonstração geradas com sucesso.`, 'success');
  }, [routes, loadSavedNotifications, saveNotifications, showToast]);

  // If initial list is completely empty, auto seed once
  useEffect(() => {
    if (!loading && notifications.length === 0) {
      handleSeedDemo();
    }
  }, [loading, notifications.length, handleSeedDemo]);

  const handleClearHistory = () => {
    if (!window.confirm('Limpar todo o histórico de notificações?')) return;
    saveNotifications([]);
    setSelectedId(null);
    showToast('Histórico de notificações limpo.', 'info');
  };

  const selectedNotification = useMemo(() => {
    if (!selectedId) return notifications[0] || null;
    return notifications.find((n) => n.id === selectedId) || notifications[0] || null;
  }, [notifications, selectedId]);

  const openPublicTracking = (trackingCode: string) => {
    window.open(`/track/${trackingCode}`, '_blank');
  };

  // Render formatted WhatsApp message with <b> tags and tracking action button
  const renderBubbleContent = (notif: WhatsAppNotification) => {
    const raw = notif.message;
    // Replace *text* with <b>text</b>
    const parts = raw.split(/(\*[^*]+\*)/g);
    const hasTrack = raw.includes('/track/');

    return (
      <>
        <div>
          {parts.map((p, idx) => {
            if (p.startsWith('*') && p.endsWith('*')) {
              return <b key={idx}>{p.slice(1, -1)}</b>;
            }
            return <React.Fragment key={idx}>{p}</React.Fragment>;
          })}
        </div>

        {hasTrack && (
          <button
            type="button"
            className="btn-track"
            onClick={() => openPublicTracking(notif.trackingCode)}
          >
            🚚 Rastrear entrega em tempo real
          </button>
        )}
      </>
    );
  };

  if (loading && notifications.length === 0) {
    return (
      <Layout>
        <div className="empty" style={{ padding: 40, textAlign: 'center' }}>
          <p>Carregando notificações...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1 className="page-title">Rastreamento e WhatsApp</h1>
          <p className="page-lede">
            Cada parada gera automaticamente um link público de rastreamento e notificações no WhatsApp do cliente final. Reduz em 15-30% os chamados "onde está meu pedido?".
          </p>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={handleSeedDemo}>
            Gerar dados demo
          </button>
          <button className="btn btn-ghost" onClick={handleClearHistory}>
            Limpar histórico
          </button>
        </div>
      </div>

      {/* Integration Status Card */}
      <div className="wa-integration-card">
        <div className="split">
          <div className="wa-integration-badge">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div className="lead">WhatsApp Business API — conectado</div>
            <div className="sub">
              Envio automático de notificações em 5 momentos-chave da entrega. Templates aprovados pela Meta e mensagens transacionais dentro da janela de 24h.
            </div>
            <div className="stat-row">
              <div className="stat-item">
                <div className="k">Notificações enviadas</div>
                <div className="v">
                  {notifications.length}
                  <span className="u">total</span>
                </div>
              </div>
              <div className="stat-item">
                <div className="k">Taxa de leitura</div>
                <div className="v">
                  94<span className="u">%</span>
                </div>
              </div>
              <div className="stat-item">
                <div className="k">Custo mensal</div>
                <div className="v">
                  R$ 148<span className="u">,00</span>
                </div>
              </div>
              <div className="stat-item">
                <div className="k">Chamados evitados</div>
                <div className="v" style={{ color: '#0d7a6c' }}>
                  −32<span className="u">%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="wa-layout">
        {/* Left Column: Notification Log */}
        <div className="wa-log">
          <div className="wa-log-head">
            <div>
              <div className="title">Log de notificações</div>
              <div className="sub">Todas as mensagens disparadas automaticamente durante as entregas</div>
            </div>
          </div>

          <div className="wa-log-body">
            {notifications.map((n) => {
              const ev = EVENT_CONFIG[n.event] || EVENT_CONFIG.route_started;
              const isSelected = selectedNotification?.id === n.id;
              return (
                <div
                  key={n.id}
                  className={`wa-notif-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedId(n.id)}
                >
                  <div className={`wa-notif-icon ${ev.cls}`}>
                    {getEventIcon(n.event)}
                  </div>
                  <div className="wa-notif-body">
                    <div className="wa-notif-name">
                      <span>{n.customerName}</span>
                      <span className="ev" style={{ background: ev.bg, color: ev.color }}>
                        {ev.label}
                      </span>
                    </div>
                    <div className="wa-notif-preview">{n.message.split('\n')[0]}</div>
                    <div className="wa-notif-meta">
                      {n.phone} · {n.orderRef} · {formatTimeAgo(n.timestamp)}
                    </div>
                  </div>
                  <div className="wa-notif-status">
                    {n.status === 'read' ? (
                      <span className="st-ico read" title="Mensagem lida">
                        <svg width="14" height="10" viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="1,7 5,11 10,4"></polyline>
                          <polyline points="6,7 10,11 15,4"></polyline>
                        </svg>
                      </span>
                    ) : n.status === 'delivered' ? (
                      <span className="st-ico" title="Mensagem entregue">
                        <svg width="14" height="10" viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="1,7 5,11 10,4"></polyline>
                          <polyline points="6,7 10,11 15,4"></polyline>
                        </svg>
                      </span>
                    ) : (
                      <span className="st-ico" title="Mensagem enviada">
                        <svg width="12" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="1,7 5,11 11,4"></polyline>
                        </svg>
                      </span>
                    )}
                    <span>{n.status === 'read' ? 'lida' : n.status === 'delivered' ? 'entregue' : 'enviada'}</span>
                  </div>
                </div>
              );
            })}

            {notifications.length === 0 && (
              <div className="wa-empty">
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                </svg>
                <h4 style={{ color: 'var(--text-2)', marginBottom: 6, fontSize: 15, fontWeight: 600 }}>Nenhuma notificação ainda</h4>
                <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 14 }}>
                  Ao iniciar uma rota no Modo Motorista, o sistema dispara automaticamente notificações para os clientes.
                </p>
                <button className="btn btn-secondary" onClick={handleSeedDemo}>
                  Gerar dados demo
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Phone Mockup Preview & Moments Guide */}
        <div>
          <div className="wa-phone-preview">
            {selectedNotification ? (
              <>
                <div className="wa-phone-header">
                  <div className="avatar">
                    {selectedNotification.customerName
                      .split(' ')
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join('')}
                  </div>
                  <div className="who">
                    <div className="name">{selectedNotification.customerName}</div>
                    <div className="status">online agora</div>
                  </div>
                  <div className="icons">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="23,7 16,12 23,17 23,7"></polygon>
                      <rect x="1" y="5" width="15" height="14" rx="2"></rect>
                    </svg>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13 1 .35 1.9.68 2.79a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.89.33 1.79.55 2.79.68A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                </div>

                <div className="wa-phone-body">
                  <div style={{ textAlign: 'center', color: '#54656f', fontSize: 11, marginBottom: 10 }}>
                    <span style={{ background: '#e1f2fb', padding: '4px 10px', borderRadius: 8 }}>
                      Express ABC · empresa comercial
                    </span>
                  </div>

                  <div className="wa-bubble out">
                    {renderBubbleContent(selectedNotification)}
                    <div className="time">
                      {new Date(selectedNotification.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      {selectedNotification.status === 'read' ? (
                        <span className="check">
                          <svg width="14" height="10" viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="1,7 5,11 10,4"></polyline>
                            <polyline points="6,7 10,11 15,4"></polyline>
                          </svg>
                        </span>
                      ) : selectedNotification.status === 'delivered' ? (
                        <span className="check" style={{ color: '#8696a0' }}>
                          <svg width="14" height="10" viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="1,7 5,11 10,4"></polyline>
                            <polyline points="6,7 10,11 15,4"></polyline>
                          </svg>
                        </span>
                      ) : (
                        <span className="check" style={{ color: '#8696a0' }}>
                          <svg width="12" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="1,7 5,11 11,4"></polyline>
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="wa-templates">
                  <div className="lbl">Detalhes desta notificação</div>
                  <div className="wa-template-item">
                    <b>Cliente:</b> {selectedNotification.customerName} · <b>Telefone:</b> {selectedNotification.phone}
                    <br />
                    <b>Pedido:</b> {selectedNotification.orderRef} · <b>Rastreamento:</b> {selectedNotification.trackingCode}
                    <br />
                    <b>Motorista:</b> {selectedNotification.driverName} · <b>Veículo:</b> {selectedNotification.vehiclePlate}
                  </div>
                </div>
              </>
            ) : (
              <div className="wa-bubble-empty">
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                </svg>
                <div>Selecione uma notificação<br />para ver a prévia</div>
              </div>
            )}
          </div>

          <div className="card" style={{ marginTop: 16, padding: 18, background: '#fff', borderRadius: 12, border: '1px solid var(--line-2)' }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: 10, color: 'var(--text-1)' }}>
              Momentos que disparam notificação
            </h4>
            <div style={{ color: 'var(--text-2)', fontSize: 12.5, lineHeight: 1.55 }}>
              <div style={{ padding: '8px 0', borderBottom: '1px dashed var(--line-2)' }}>
                <b style={{ color: 'var(--teal-800)' }}>Rota iniciada</b>
                <br />
                <span style={{ color: 'var(--text-3)' }}>Todos os clientes do roteiro recebem ETA inicial e link público</span>
              </div>
              <div style={{ padding: '8px 0', borderBottom: '1px dashed var(--line-2)' }}>
                <b style={{ color: '#a97f22' }}>Próxima parada</b>
                <br />
                <span style={{ color: 'var(--text-3)' }}>O cliente a caminho recebe alerta 15min antes</span>
              </div>
              <div style={{ padding: '8px 0', borderBottom: '1px dashed var(--line-2)' }}>
                <b style={{ color: '#0d7a6c' }}>Motorista chegou</b>
                <br />
                <span style={{ color: 'var(--text-3)' }}>Alerta imediato para acelerar o atendimento</span>
              </div>
              <div style={{ padding: '8px 0', borderBottom: '1px dashed var(--line-2)' }}>
                <b style={{ color: '#128c7e' }}>Pedido entregue</b>
                <br />
                <span style={{ color: 'var(--text-3)' }}>Confirmação com link de avaliação e NPS</span>
              </div>
              <div style={{ padding: '8px 0' }}>
                <b style={{ color: '#c1573b' }}>Atraso detectado</b>
                <br />
                <span style={{ color: 'var(--text-3)' }}>Se ETA + trânsito ultrapassar a janela de horário cadastrada</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
